if (!customElements.get('infinite-scroll')) {
  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      constructor() {
        super();
        this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
        this.spinner = this.querySelector('.infinite-scroll__spinner');
        this.loadMoreButton = this.querySelector('[data-infinite-scroll-load-more]');
        this.loading = false;
        this.loadedPages = 0;
        this.restoring = false;
        this.buttonMode = false;
        this.storageKey = `infinite-scroll:${window.location.pathname}${window.location.search}`;
        this.savePosition = this.savePosition.bind(this);
        this.onLoadMoreClick = this.onLoadMoreClick.bind(this);
      }

      get maxAutoLoads() {
        const value = Number.parseInt(this.dataset.maxAutoLoads || '2', 10);
        return Number.isFinite(value) && value >= 0 ? value : 2;
      }

      connectedCallback() {
        if (!('IntersectionObserver' in window) || !this.sentinel) return;

        window.addEventListener('pagehide', this.savePosition);
        document.addEventListener('click', this.savePosition, true);
        if (this.loadMoreButton) {
          this.loadMoreButton.addEventListener('click', this.onLoadMoreClick);
        }

        if (this.shouldRestore()) {
          this.restoreState();
        } else {
          this.clearState();
          this.createObserver();
        }
      }

      shouldRestore() {
        if (!this.getState()) return false;
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav && nav.type) return nav.type === 'back_forward';
        // Fallback for older browsers
        return performance.navigation && performance.navigation.type === 2;
      }

      getState() {
        try {
          const raw = window.sessionStorage.getItem(this.storageKey);
          return raw ? JSON.parse(raw) : null;
        } catch (error) {
          return null;
        }
      }

      setState(state) {
        try {
          window.sessionStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch (error) {
          /* sessionStorage unavailable */
        }
      }

      clearState() {
        try {
          window.sessionStorage.removeItem(this.storageKey);
        } catch (error) {
          /* sessionStorage unavailable */
        }
      }

      savePosition(event) {
        let href = '';
        // When triggered by a click, only persist if a product link was clicked.
        if (event && event.type === 'click') {
          const list = this.getList(document);
          const link = event.target.closest('a[href]');
          if (!list || !link || !list.contains(link)) return;
          href = link.getAttribute('href');
        }
        if (this.restoring) return;
        this.setState({ pages: this.loadedPages, scrollY: window.scrollY, href: href });
      }

      async restoreState() {
        const state = this.getState();
        if (!state) {
          this.createObserver();
          return;
        }

        this.restoring = true;
        if ('scrollRestoration' in window.history) {
          window.history.scrollRestoration = 'manual';
        }
        this.showOverlay();

        for (let i = 0; i < state.pages && this.nextUrl; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await this.loadNext(true);
        }

        this.restoreScroll(state);
        this.hideOverlay();

        this.restoring = false;
        this.clearState();
        if (this.nextUrl) this.continueAfterLoad();
      }

      restoreScroll(state) {
        const target = state.href ? this.findCardByHref(state.href) : null;
        if (target) {
          target.scrollIntoView({ block: 'center' });
          requestAnimationFrame(() => target.scrollIntoView({ block: 'center' }));
          return;
        }
        window.scrollTo(0, state.scrollY || 0);
        // A second scroll on the next frame compensates for late layout shifts (lazy images).
        requestAnimationFrame(() => window.scrollTo(0, state.scrollY || 0));
      }

      findCardByHref(href) {
        const list = this.getList(document);
        if (!list) return null;
        const path = href.split('?')[0];
        const link =
          list.querySelector(`a[href="${href}"]`) ||
          list.querySelector(`a[href^="${path}?"]`) ||
          list.querySelector(`a[href="${path}"]`);
        return link ? link.closest('li') || link : null;
      }

      showOverlay() {
        if (!this.overlay) {
          this.overlay = document.createElement('div');
          this.overlay.className = 'infinite-scroll__overlay';
          this.overlay.setAttribute('role', 'status');
          this.overlay.setAttribute('aria-live', 'polite');
          this.overlay.innerHTML =
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 66 66">' +
            '<circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>' +
            '<p class="infinite-scroll__overlay-text">Carregando produtos…</p>';
          document.body.appendChild(this.overlay);
        }
        // Force reflow so the transition runs.
        void this.overlay.offsetWidth;
        this.overlay.classList.add('is-visible');
      }

      hideOverlay() {
        if (!this.overlay) return;
        this.overlay.classList.remove('is-visible');
      }

      canAutoLoad() {
        return !this.buttonMode && this.loadedPages < this.maxAutoLoads;
      }

      continueAfterLoad() {
        if (!this.nextUrl) {
          this.stop();
          return;
        }

        if (this.canAutoLoad()) {
          this.hideLoadMore();
          this.createObserver();
          return;
        }

        this.enterButtonMode();
      }

      enterButtonMode() {
        this.buttonMode = true;
        this.classList.add('infinite-scroll--button-mode');
        if (this.observer) this.observer.disconnect();
        this.showLoadMore();
      }

      showLoadMore() {
        if (!this.loadMoreButton || !this.nextUrl) return;
        this.loadMoreButton.hidden = false;
        this.loadMoreButton.disabled = false;
      }

      hideLoadMore() {
        if (!this.loadMoreButton) return;
        this.loadMoreButton.hidden = true;
      }

      onLoadMoreClick(event) {
        event.preventDefault();
        if (this.loading || !this.nextUrl) return;
        this.buttonMode = true;
        this.loadNext();
      }

      createObserver() {
        if (this.observer) this.observer.disconnect();
        const list = this.getList(document);
        const margin = Math.max(400, list ? Math.round(list.offsetHeight / 2) : 0);
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!this.canAutoLoad()) {
                this.enterButtonMode();
                return;
              }
              if (entry.isIntersecting || entry.boundingClientRect.top < 0) this.loadNext();
            });
          },
          { rootMargin: `0px 0px ${margin}px 0px` }
        );
        this.observer.observe(this.sentinel);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        window.removeEventListener('pagehide', this.savePosition);
        document.removeEventListener('click', this.savePosition, true);
        if (this.loadMoreButton) {
          this.loadMoreButton.removeEventListener('click', this.onLoadMoreClick);
        }
      }

      get nextUrl() {
        return this.dataset.nextUrl || '';
      }

      getList(root) {
        const grid = root.getElementById('product-grid');
        if (!grid) return null;
        return grid.tagName === 'UL' ? grid : grid.querySelector('ul.product-grid');
      }

      async loadNext(silent) {
        if (this.loading || !this.nextUrl) return;
        this.loading = true;
        if (this.loadMoreButton) this.loadMoreButton.disabled = true;
        if (this.spinner && !silent) this.spinner.removeAttribute('hidden');
        if (!silent) this.hideLoadMore();

        try {
          const response = await fetch(this.nextUrl);
          if (!response.ok) throw new Error(`Infinite scroll request failed with status ${response.status}`);

          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const newList = this.getList(doc);
          const currentList = this.getList(document);

          if (newList && currentList) {
            newList.querySelectorAll(':scope > li').forEach((item) => {
              item.classList.remove('scroll-trigger', 'animate--slide-in', 'scroll-trigger--offscreen');
              item.removeAttribute('data-cascade');
              item.removeAttribute('style');
              currentList.appendChild(item);
            });
          }

          this.loadedPages += 1;

          const nextEl = doc.querySelector('infinite-scroll');
          const next = nextEl ? nextEl.dataset.nextUrl : '';
          if (next) {
            this.dataset.nextUrl = next;
            if (!this.restoring) this.continueAfterLoad();
          } else {
            this.stop();
          }
        } catch (error) {
          console.error('[infinite-scroll]', error);
          this.stop();
        } finally {
          this.loading = false;
          if (this.spinner && !silent) this.spinner.setAttribute('hidden', '');
          if (this.loadMoreButton && this.nextUrl && this.buttonMode) {
            this.loadMoreButton.disabled = false;
          }
        }
      }

      stop() {
        this.dataset.nextUrl = '';
        if (this.observer) this.observer.disconnect();
        this.hideLoadMore();
      }
    }
  );
}
